import React, { useEffect, useState } from "react"
import ReactDOM from "react-dom/client"
import { BrowserRouter } from "react-router-dom"
import { HelmetProvider } from "react-helmet-async"
import { AnimatePresence, motion } from "framer-motion"

import App from "@/app/App"

import "@/index.css"

/*  LOADER MESSAGES */
const messages = [
  "Preparing your stay experience",
  "Optimizing property performance",
  "Loading curated homes",
]

/* PREMIUM APP LOADER */
function AppLoader() {
  const [msgIndex, setMsgIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex((prev) => (prev + 1) % messages.length)
    }, 900)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-surface">
      <div className="flex flex-col items-center gap-6">

        {/*  Animated Bars */}
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-[4px] rounded-full bg-brand-primary"
              initial={{ height: 6 }}
              animate={{ height: [6, 18, 6] }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.15,
              }}
            />
          ))}
        </div>

        {/* Animated Progress Line */}
        <div className="relative h-[3px] w-44 overflow-hidden rounded-full bg-bg-light">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent"
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </div>

        {/*  Dynamic Message */}
        <AnimatePresence mode="wait">
          <motion.p
            key={msgIndex}
            className="text-center text-xs text-text-muted"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
          >
            {messages[msgIndex]}
          </motion.p>
        </AnimatePresence>

      </div>
    </div>
  )
}

/* APP ROOT */
function Root() {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false)
    }, 250)

    return () => clearTimeout(timer)
  }, [])

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <motion.div
          key="loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <AppLoader />
        </motion.div>
      ) : (
        <motion.div
          key="app"
          initial={{ opacity: 0, scale: 0.995 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.25,
            ease: "easeOut",
          }}
        >
          <HelmetProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </HelmetProvider>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

ReactDOM.createRoot(
  document.getElementById("root")
).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
)